import Head from 'next/head';
import { AcfMediaFragment } from '@/__generated__/graphql';
import { toAbsoluteProxiedWpUrl } from '@/utils/wpMediaUrl';

/**
 * Provide SEO related meta tags to a page.
 *
 * @param {Props} props The props object.
 * @param {string} props.title Used for the page title, og:title, twitter:title, etc.
 * @param {string} props.description Used for the meta description, og:description, twitter:description, etc.
 * @param {string} props.imageUrl Used for the og:image and twitter:image. NOTE: Must be an absolute url.
 * @param {string} props.url Used for the og:url and twitter:url.
 * @param {object|object[]} props.jsonLd One or more schema.org objects, emitted as ld+json in <head>.
 *
 * @returns {React.ReactElement} The SEO component
 */
type SEOProps = {
  title?: string;
  description?: string;
  image?: AcfMediaFragment;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

export default function SEO({ title, description, image, jsonLd }: SEOProps) {
  const jsonLdBlocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  if (!title && !description && !image && jsonLdBlocks.length === 0) return null;
  const imageUrl = toAbsoluteProxiedWpUrl(image?.node?.mediaItemUrl) || null;

  return (
    <>
      <Head>
        <meta property="og:type" content="website" />
        <meta property="twitter:card" content="summary_large_image" />

        {title && (
          <>
            <title>{title}</title>
            <meta name="title" content={title} />
            <meta property="og:title" content={title} />
            <meta property="twitter:title" content={title} />
          </>
        )}

        {description && (
          <>
            <meta name="description" content={description} />
            <meta property="og:description" content={description} />
            <meta property="twitter:description" content={description} />
          </>
        )}

        {imageUrl && (
          <>
            <meta property="og:image" content={imageUrl} />
            <meta property="twitter:image" content={imageUrl} />
          </>
        )}

        {jsonLdBlocks.map((block, i) => (
          <script
            key={`jsonld-${i}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
          />
        ))}
      </Head>
    </>
  );
}
